import { Button, CodeEntry, Divider, Grid, Heading, IconNames, Option, Text, usePhoneNumber, useToast } from '@afterpaytouch/core'
import clsx from 'clsx'
import { Trans, useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, ReactElement, useEffect, useRef, useState, ReactNode } from 'react'
import { FormWrapper, ProfileCard, BackLink, TransMaskedText } from '..'
import { SEND_TIMEOUT_MS } from '../../config'
import { Route } from '../../config/router'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import {
  CodeVerificationDirective,
  Features,
  LoginMethodTypes,
  VerificationError,
  VerificationErrorCode,
  VerificationFlow,
  VerificationFlowContext,
  VerificationFlowRoute,
  VerificationMethod,
  VerifyRegistrationSMSAuthorizationCode,
  WaitingForEvents,
} from '../../model'
import { TrackingEvent, TrackingEvents } from '../../model/monitoring'
import {
  resetCheckoutError,
  useAppDispatch,
  useAppRouter,
  useConsumerSelector,
  useCountry,
  useIsApplePaylaterSkipPasswordCreation,
  useLogLoginAttemptEvent,
  verifyRegistrationMobileEventError,
  verifyRegistrationMobileEventSuccess,
} from '../../state'
import { maskPhoneNumber } from '../../utils/phone-numbers'
import { useCheckoutSizes } from '../../utils/responsive'
import { useSet } from '../../utils/hooks'
import styles from './styles.module.css'
import { handleVerificationError, getErrorMessage, convertAuthzErrorEventToVerificationError, getErrorToastTransKey } from './helper'
import { Verifying } from './Verifying'
import { FetchVerificationWaitResponse, FetchVerificationErrorResponse, FetchVerificationSuccessResponse } from '@afterpaytouch/event-verify'
import { useProfileCardProps, useVerifyTimeout } from './hooks'
import { Description } from './Description'
import { useFlag } from '@afterpaytouch/feature'
import { ErrorCode } from '@afterpaytouch/portal-api'

interface Props {
  flow: VerificationFlow
  disabled: boolean
  onSuccess: (result?: VerificationFlowRoute) => void
  resetDisabledState: () => void
  onTerminalError: (error?: VerificationErrorCode) => void
  onComplete: () => void
  onError?: (error?: VerificationErrorCode) => void
}

interface SendCodeLinkProps {
  children: ReactNode
}

const icons: Record<VerificationMethod, IconNames> = {
  EMAIL: 'Email',
  SMS: 'Phone',
}

const trackingEvents: Record<VerificationMethod, TrackingEvents> = {
  EMAIL: 'PRESSED_VERIFY_EMAIL_BUTTON',
  SMS: 'PRESSED_VERIFY_SMS_BUTTON',
}

export const Verify: FunctionComponent<Props> = ({
  flow: {
    previousRoute,
    sendCode,
    checkCode,
    enabled,
    autoSendCode,
    allowLogout,
    revealMobile,
    initialMethod,
    context,
    sendSmsCodeEvent,
    sendSmsAuthorization,
  },
  disabled,
  onSuccess,
  resetDisabledState,
  onTerminalError,
  onComplete,
  onError = () => {},
}) => {
  const { t } = useTranslation()
  const { email, mobile: consumerMobile } = useConsumerSelector()
  const [method, setMethod] = useState<VerificationMethod | undefined>(initialMethod)
  const [codeEntryReset, setCodeEntryReset] = useState(0)
  const [codeEntryErrorMessage, setCodeEntryErrorMessage] = useState('')
  const setToast = useToast()
  const [allowSend, { add: addToAllowSend, remove: removeFromAllowSend }] = useSet<VerificationMethod>([VerificationMethod.EMAIL])
  const [wasAutoSent, setWasAutoSent] = useState(false)
  const [disableCodeEntry, setDisableCodeEntry] = useState(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { textSize, headingSize } = useCheckoutSizes()
  const country = useCountry()
  const { formatNational } = usePhoneNumber({ country })
  const codeEntryRef = useRef<HTMLInputElement | undefined>()
  const logLoginAttemptEvent = useLogLoginAttemptEvent({ is2fa: true })
  const profileCardProps = useProfileCardProps()
  const isVerifySmsDelayEnabled = useFlag(Features.VERIFY_SMS_DELAY, false)
  const isApplePaylaterSkipPasswordCreation = useIsApplePaylaterSkipPasswordCreation()
  const [smsDelayMs, setSmsDelayMs] = useState(0)
  const [sendingCode, setSendingCode] = useState(autoSendCode)
  const [isSkipCodeVerification, setIsSkipCodeVerification] = useState(false)
  const [fetchingSmsDelay, setFetchingSmsDelay] = useState(false)
  const dispatch = useAppDispatch()
  const router = useAppRouter()

  const mobile = revealMobile ? formatNational(consumerMobile ?? '') : maskPhoneNumber(consumerMobile)

  const { waitingFor, verifyTimeout, startVerifyTimeoutTimer, killVerifyTimeoutTimer } = useVerifyTimeout()
  const showLoadingScreen = smsDelayMs > 0 || fetchingSmsDelay || sendingCode || isSkipCodeVerification

  const onCodeComplete = async (code: string): Promise<void> => {
    if (method === undefined) {
      return
    }

    onComplete()
    setDisableCodeEntry(true)

    const loginMethod = TrackingEvent.LOGIN_WEB_PASSWORD as LoginMethodTypes
    try {
      setCodeEntryErrorMessage('')
      const checkCodeResult = await checkCode(code, method)
      onSuccess(checkCodeResult)
      if (context === VerificationFlowContext.Efa) {
        logEvent(TrackingEvent.EFA_VERIFICATION_SUCCESS)
      } else {
        logLoginAttemptEvent(loginMethod, true)
      }
    } catch (e) {
      handleVerificationError({ e, logEvent, onTerminalError, onError })
      const codeEntryErrorMessage = t(getErrorMessage(e), { context })
      setCodeEntryErrorMessage(codeEntryErrorMessage)
      resetDisabledState()
      setDisableCodeEntry(false)
      setCodeEntryReset(codeEntryReset + 1)
      if (context !== VerificationFlowContext.Efa) {
        logLoginAttemptEvent(loginMethod, false)
      }
    }
  }
  const sendSmsCodeSuccess = async (result?: CodeVerificationDirective | void): Promise<void> => {
    setSendingCode(false)

    // There are times we want to skip showing a code sent toast
    // For example, skipping sms or email verification
    if (result !== null && result !== undefined && result.skipCodeVerification) {
      setIsSkipCodeVerification(true)
      // short circuit into check code if skipping
      await onCodeComplete('111111')
      return
    }

    setCodeEntryReset(codeEntryReset + 1)
    setSmsDelayMs(0)
    setFetchingSmsDelay(false)

    setToast({
      message: t('verify:codeSent'),
      kind: 'Success',
      onShow: () => logEvent(TrackingEvent.VIEWED_VERIFY_SENT_TOAST, { mobile: maskPhoneNumber(mobile) }),
    })
  }

  useEffect(() => {
    if (verifyTimeout) {
      handleVerifyTimeOutError()
    }
  }, [verifyTimeout])

  const failedToSendCodeToastMessage = (e: VerificationError, method: VerificationMethod): ReactNode => {
    const key = getErrorToastTransKey(e, context, method ?? VerificationMethod.SMS)
    return (
      <Text>
        <Trans
          i18nKey={key}
          components={{
            mobile: <TransMaskedText actionName='Verify Code Mobile Number' text={mobile} testNameSpace='toast-verify-code-sent-fail-mobile' />,
            email: <TransMaskedText actionName='Verify Code Email' text={email ?? ''} testNameSpace='toast-verify-code-sent-fail-email' />,
          }}
        />
      </Text>
    )
  }

  const sendSmsCodeEventSuccess = async (e: FetchVerificationSuccessResponse): Promise<void> => {
    killVerifyTimeoutTimer()
    dispatch(verifyRegistrationMobileEventSuccess())
    await sendSmsCodeSuccess(e)
  }

  const sendSmsCodeEventError = (e: FetchVerificationErrorResponse): void => {
    killVerifyTimeoutTimer()
    try {
      const error = convertAuthzErrorEventToVerificationError(e)
      handleSendCodeError(VerificationMethod.SMS, error)
      dispatch(verifyRegistrationMobileEventError({ errorCode: e.message?.toLowerCase() as ErrorCode }))
    } catch (e) {
      onTerminalError()
    }
  }

  const sendSmsCodeEventWaiting = (d: FetchVerificationWaitResponse): void => {
    killVerifyTimeoutTimer()
    const delayMs = d.delayMs ?? 0
    setSendingCode(true)
    setSmsDelayMs(delayMs)
    setFetchingSmsDelay(false)
    startVerifyTimeoutTimer(WaitingForEvents.SUCCESS, delayMs)
    logEvent(TrackingEvent.TRIGGERED_SMS_VERIFY_TIME_DELAY, {
      waitTime: `${delayMs / 1000} s`,
    })
  }

  const handleVerifyTimeOutError = (): void => {
    setToast({
      message: t('verify:error:timeout'),
      kind: 'Failure',
      onShow: () =>
        logEvent(TrackingEvent.VIEWED_VERIFY_PAGE_TIMEOUT_TOAST, {
          waitingFor,
        }),
    })
    return onError()
  }

  const handleSendCodeError = (method, e): void => {
    setSendingCode(false)

    if (VerificationError.is(e)) {
      if (e.isFatal) {
        return onTerminalError(e.code)
      }

      if (e.isSignUpError) {
        if (
          e.code === VerificationErrorCode.ExistingPhoneNumber ||
          e.code === VerificationErrorCode.ExistingPhoneNumberSendEmail ||
          e.code === VerificationErrorCode.ExistingPhoneNumberUnicorn
        ) {
          logEvent(TrackingEvent.VIEWED_EXISTING_MOBILE_NUMBER_ERROR)
        }
        return onError(e.code)
      }
      if (e.isEfaNotRequired) {
        dispatch(resetCheckoutError())
        router.push(Route.SUMMARY)
      }
    }
    setToast({
      message: () => failedToSendCodeToastMessage(e, method),
      kind: 'Failure',
      onShow: () => logEvent(TrackingEvent.VIEWED_VERIFY_FAIL_TOAST, { mobile: maskPhoneNumber(mobile) }),
    })

    if (context === VerificationFlowContext.Efa) {
      addToAllowSend([VerificationMethod.SMS])
    } else {
      return onError()
    }
  }

  const sendBy = async (sendBy: VerificationMethod, disableAll: boolean = true): Promise<void> => {
    const triggerVerifyCallAuthz = sendSmsAuthorization !== undefined
    const triggerVerifySmsDelay = isVerifySmsDelayEnabled && sendBy === 'SMS' && sendSmsCodeEvent !== undefined
    try {
      setCodeEntryErrorMessage('')
      setMethod(sendBy)
      removeFromAllowSend(disableAll ? Object.values(VerificationMethod) : [sendBy])
      // TODO clean FF code once it's fully roll out. https://apglobal.atlassian.net/browse/CHECKOUT-3496
      if (triggerVerifyCallAuthz) {
        setFetchingSmsDelay(true)
        const result = await sendSmsAuthorization()
        if (result.code === VerifyRegistrationSMSAuthorizationCode.FAILED) {
          logEvent(TrackingEvent.VIEWED_VERIFY_PAGE_FAIL_TOAST, {
            errorCode: result.code,
            errorMessage: result.message,
          })
          return onTerminalError()
        }
        // TODO remove this logic once sms delay is enabled
        if (!triggerVerifySmsDelay) {
          setFetchingSmsDelay(false)
        }
      }
      if (triggerVerifySmsDelay) {
        await sendSmsCodeEvent({
          onSuccess: sendSmsCodeEventSuccess,
          onError: sendSmsCodeEventError,
          onWaiting: sendSmsCodeEventWaiting,
        })
        startVerifyTimeoutTimer(WaitingForEvents.WAIT)
      } else {
        setSendingCode(true)
        const sendCodeResult = await sendCode(sendBy)
        sendSmsCodeSuccess(sendCodeResult)
      }
    } catch (e) {
      handleSendCodeError(sendBy, e)
    }
  }

  const onClick = (m: VerificationMethod) => () => {
    logEvent(TrackingEvent[trackingEvents[m]])
    setWasAutoSent(false)
    sendBy(m)
  }

  useEffect(() => {
    if (!enabled.SMS && !enabled.EMAIL) {
      return onTerminalError(VerificationErrorCode.MethodNotEnabled)
    }

    if (autoSendCode) {
      setWasAutoSent(true)
      if (enabled.SMS) {
        sendBy(VerificationMethod.SMS, false)
      } else if (enabled.EMAIL) {
        sendBy(VerificationMethod.EMAIL, false)
      }
    }
  }, [])

  useEffect(() => {
    const input = codeEntryRef.current
    let focusTimeout
    if (input != null) {
      focusTimeout = setTimeout(() => input.focus(), 250)
    }
    return () => clearTimeout(focusTimeout)
  }, [codeEntryRef.current])

  useEffect(() => {
    if (allowSend.size < Object.keys(VerificationMethod).length) {
      const t = setTimeout(() => addToAllowSend(Object.values(VerificationMethod)), SEND_TIMEOUT_MS)
      return () => clearTimeout(t)
    }
  }, [allowSend])

  const chooseMethod = (): ReactNode => {
    const renderOption = (m: VerificationMethod): ReactNode =>
      enabled[m] ? (
        <Option
          onClick={onClick(m)}
          icon={icons[m]}
          testNameSpace={`choose-${m.toLowerCase()}`}
          text={t(`verify:${m.toLowerCase()}.sendOption`, { mobile, email })}
        />
      ) : null

    return (
      <>
        <Grid>
          <Text>{t('verify:chooseDescription')}</Text>
        </Grid>
        <Grid>
          <Divider kind='Dashed' />
        </Grid>
        <Grid>
          <Heading size='S'>{t('verify:chooseMethod')}</Heading>
        </Grid>
        <Grid>
          <div>
            {renderOption(VerificationMethod.SMS)}
            {renderOption(VerificationMethod.EMAIL)}
          </div>
        </Grid>
      </>
    )
  }

  const isRevealMobile = method === VerificationMethod.SMS && revealMobile

  const enterCode = (): ReactElement => {
    const SendCodeLink = (m: VerificationMethod): FunctionComponent => {
      const component: FunctionComponent<SendCodeLinkProps> = ({ children }) =>
        enabled[m] ? (
          <Button.Link size='Regular' disabled={disabled || !allowSend.has(m)} onClick={onClick(m)}>
            {children}
          </Button.Link>
        ) : null
      component.displayName = `Send${m}Link`
      return component
    }
    const SendSMS = SendCodeLink(VerificationMethod.SMS)
    const SendEmail = SendCodeLink(VerificationMethod.EMAIL)
    const components = { sms: <SendSMS />, email: <SendEmail /> }
    const isOnlyMethod = !Object.keys(VerificationMethod)
      .filter((k) => k !== method)
      .some((k) => enabled[k])

    return (
      <>
        <Description method={method} isRevealMobile={isRevealMobile} mobile={mobile} email={email} wasAutoSent={wasAutoSent} context={context} />
        <Grid>
          <CodeEntry
            name='verify-code'
            aria-labelledby='verify-code'
            codeEntryLength={6}
            disabled={disabled || disableCodeEntry}
            error={codeEntryErrorMessage !== ''}
            errorText={codeEntryErrorMessage}
            key={codeEntryReset}
            onComplete={onCodeComplete}
            testNameSpace='verify'
            ref={codeEntryRef}
          />
        </Grid>
        <Grid>
          <div className={clsx(styles.resendLinkContainer)}>
            <Text size={textSize}>
              <Trans i18nKey={`verify:${method?.toLowerCase()}.sendLink`} context={isOnlyMethod ? 'only' : ''} components={components} />
            </Text>
          </div>
        </Grid>
      </>
    )
  }

  return showLoadingScreen ? (
    <Verifying allowLogout={allowLogout} isRevealMobile={isRevealMobile} delayMs={smsDelayMs} />
  ) : (
    <>
      {previousRoute !== undefined && <BackLink backTo={previousRoute} disabled={disabled} />}
      {!isApplePaylaterSkipPasswordCreation && (
        <Grid>
          <Heading size={headingSize}>{t('verify:heading', { context })}</Heading>
        </Grid>
      )}
      <FormWrapper>
        <ProfileCard allowLogout={allowLogout} revealMobile={isRevealMobile} {...profileCardProps} />
        {method === undefined ? chooseMethod() : enterCode()}
      </FormWrapper>
    </>
  )
}
